import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, getAdminToken, clearAdminToken } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Loader2, FileText, Download, Send, Clock } from "lucide-react";

const STATUSES = [
    { v: "new", l: "New", color: "#60A5FA" },
    { v: "in_review", l: "In review", color: "#D4AF37" },
    { v: "approved", l: "Approved", color: "#4ADE80" },
    { v: "rejected", l: "Rejected", color: "#F87171" },
];

function slotLabel(slot) {
    return (
        {
            license_front: "License — front",
            license_back: "License — back",
            ssn_document: "SSN document",
            primary_bill: "Primary bill",
            secondary_bill: "Secondary bill",
            extra_1: "Extra document 1",
            extra_2: "Extra document 2",
            extra_3: "Extra document 3",
        }[slot] || "Document"
    );
}

function Row({ label, value }) {
    return (
        <div className="flex items-start justify-between gap-4 py-2 border-b border-white/5 last:border-0">
            <div className="field-label">{label}</div>
            <div className="text-sm text-white/90 text-right font-sans break-words max-w-[60%]">{value || <span className="text-white/30">—</span>}</div>
        </div>
    );
}

export default function AdminSubmissionDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState(null);
    const [note, setNote] = useState("");
    const [savingNote, setSavingNote] = useState(false);
    const [savingStatus, setSavingStatus] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get(`/admin/submissions/${id}`);
            setData(data);
        } catch (e) {
            if (e?.response?.status === 401) {
                clearAdminToken();
                navigate("/admin/login");
            } else {
                toast.error("Could not load submission");
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line
    }, [id]);

    const changeStatus = async (s) => {
        setSavingStatus(true);
        try {
            await api.patch(`/admin/submissions/${id}/status`, { status: s });
            toast.success(`Status updated to ${s}`);
            load();
        } catch {
            toast.error("Could not update status");
        } finally {
            setSavingStatus(false);
        }
    };

    const addNote = async () => {
        if (!note.trim()) return;
        setSavingNote(true);
        try {
            await api.post(`/admin/submissions/${id}/notes`, { text: note.trim() });
            setNote("");
            toast.success("Note added");
            load();
        } catch {
            toast.error("Could not add note");
        } finally {
            setSavingNote(false);
        }
    };

    const fileUrl = (fileId) => {
        const token = getAdminToken();
        return `${process.env.REACT_APP_BACKEND_URL}/api/admin/submissions/${id}/files/${fileId}?auth=${encodeURIComponent(token)}`;
    };

    const openFile = (fileId) => {
        window.open(fileUrl(fileId), "_blank", "noopener");
    };

    if (loading || !data) {
        return (
            <div className="min-h-screen luxury-bg flex items-center justify-center" data-testid="detail-loading">
                <Loader2 className="animate-spin text-[#D4AF37]" />
            </div>
        );
    }

    const p = data.form_payload?.personal || {};
    const ded = data.form_payload?.dedicated_email || {};
    const bureaus = data.form_payload?.bureau_login || {};
    const fin = data.form_payload?.finalize || {};

    return (
        <div className="min-h-screen luxury-bg text-white pb-16" data-testid="admin-detail-page">
            <header className="border-b border-[#D4AF37]/20 bg-[#0a1628]/80 backdrop-blur-xl sticky top-0 z-30">
                <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
                    <Link to="/admin" className="text-sm text-white/80 hover:text-[#D4AF37] inline-flex items-center gap-2" data-testid="back-to-dashboard">
                        <ArrowLeft size={16} /> All submissions
                    </Link>
                    <div className="text-xs text-white/50 font-mono" data-testid="detail-submission-id">
                        {data.submission_id}
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-6 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left column: applicant info */}
                <section className="lg:col-span-2 space-y-6">
                    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                        <div className="flex items-start justify-between gap-4 flex-wrap">
                            <div>
                                <div className="field-label">Applicant</div>
                                <h1 className="font-display text-4xl mt-1 text-white" data-testid="applicant-name">
                                    {p.first_name} {p.last_name}
                                </h1>
                                {p.preferred_name && <p className="text-white/55 text-sm mt-1">Goes by {p.preferred_name}</p>}
                            </div>
                            <div className="flex gap-2 flex-wrap">
                                {STATUSES.map((s) => (
                                    <button
                                        key={s.v}
                                        onClick={() => changeStatus(s.v)}
                                        disabled={savingStatus || data.status === s.v}
                                        className={`px-3 py-1.5 rounded-full text-xs border transition ${
                                            data.status === s.v
                                                ? "border-[#D4AF37]/60 bg-[#D4AF37]/15 text-white"
                                                : "border-white/10 bg-white/[0.02] text-white/70 hover:border-white/30"
                                        }`}
                                        data-testid={`status-${s.v}-btn`}
                                    >
                                        {s.l}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="detail-personal">
                        <h2 className="font-display text-xl mb-3">Personal</h2>
                        <Row label="DOB" value={p.dob} />
                        <Row label="Address" value={[p.street, p.city, p.state, p.zip].filter(Boolean).join(", ")} />
                        <Row label="SSN" value={p.ssn || data.applicant?.ssn_full || (data.applicant?.ssn_last4 ? `***-**-${data.applicant.ssn_last4}` : "")} />
                        <Row label="Mobile" value={p.mobile} />
                        <Row label="Email" value={p.email} />
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="detail-dedicated-email">
                        <h2 className="font-display text-xl mb-3">Dedicated Email Access</h2>
                        {ded.need_help ? (
                            <p className="text-sm text-white/75">Help requested — applicant asked the team to assist with email setup.</p>
                        ) : (
                            <>
                                <Row label="Email" value={ded.new_email} />
                                <Row label="Password" value={ded.password || ""} />
                                <Row label="Consent" value={ded.agree ? "Agreed" : "Not agreed"} />
                            </>
                        )}
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="detail-bureaus">
                        <h2 className="font-display text-xl mb-3">Credit Bureau Logins</h2>
                        {["experian", "equifax", "transunion"].map((id) => {
                            const b = bureaus[id] || {};
                            const color = { experian: "#4F8EE8", equifax: "#E13B3B", transunion: "#2BB673" }[id];
                            return (
                                <div key={id} className="py-3 border-b border-white/5 last:border-0">
                                    <div className="font-display text-base mb-2" style={{ color }}>
                                        {id === "experian" ? "Experian" : id === "equifax" ? "Equifax" : "TransUnion"}
                                    </div>
                                    {b.need_help ? (
                                        <p className="text-sm text-white/70">Assistance requested.</p>
                                    ) : (
                                        <div className="text-sm text-white/85 space-y-1">
                                            <div><span className="text-white/40 text-xs uppercase tracking-wide">Username: </span>{b.username || "—"}</div>
                                            <div><span className="text-white/40 text-xs uppercase tracking-wide">Password: </span>{b.password || "—"}</div>
                                            {id === "experian" && (
                                                <>
                                                    <div><span className="text-white/40 text-xs uppercase tracking-wide">Security Q: </span>{b.security_question || "—"}</div>
                                                    <div><span className="text-white/40 text-xs uppercase tracking-wide">Security A: </span>{b.security_answer || "—"}</div>
                                                    <div><span className="text-white/40 text-xs uppercase tracking-wide">PIN: </span>{b.pin || "—"}</div>
                                                </>
                                            )}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                        {bureaus.contact_preference && (
                            <div className="pt-3 text-sm text-white/75">
                                <span className="text-white/40 text-xs uppercase tracking-wide mr-2">Contact preference:</span>
                                {bureaus.contact_preference === "text" ? "Text message" : "Phone call"}
                            </div>
                        )}
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="detail-signature">
                        <h2 className="font-display text-xl mb-3">Authorization</h2>
                        <Row label="Date" value={fin.date} />
                        <Row label="Confirmed" value={fin.confirm ? "Yes" : "No"} />
                        {fin.signature && (
                            <div className="mt-3">
                                <div className="field-label mb-2">Signature</div>
                                <img src={fin.signature} alt="signature" className="bg-white rounded-md p-2 max-w-md border border-white/10" />
                            </div>
                        )}
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="detail-files">
                        <h2 className="font-display text-xl mb-3">Documents</h2>
                        {(data.uploaded_files || []).length === 0 ? (
                            <p className="text-sm text-white/40">No documents uploaded.</p>
                        ) : (
                            <ul className="space-y-4">
                                {data.uploaded_files.map((f) => {
                                    const isImage = (f.content_type || "").startsWith("image/");
                                    const isPdf = f.content_type === "application/pdf";
                                    const url = fileUrl(f.file_id);
                                    return (
                                        <li
                                            key={f.file_id}
                                            className="rounded-xl border border-white/10 bg-white/[0.03] overflow-hidden"
                                            data-testid={`file-${f.file_id}`}
                                        >
                                            <div className="flex items-center justify-between gap-3 px-4 py-3">
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                                                        <FileText size={16} className="text-[#D4AF37]" />
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37]/80">
                                                            {f.slot ? slotLabel(f.slot) : "Document"}
                                                        </div>
                                                        <div className="text-sm text-white/90 truncate">{f.original_filename}</div>
                                                        <div className="text-xs text-white/40">{(f.size / 1024).toFixed(0)} KB · {f.content_type}</div>
                                                    </div>
                                                </div>
                                                <button
                                                    onClick={() => openFile(f.file_id)}
                                                    className="btn-ghost-light px-3 py-1.5 text-xs inline-flex items-center gap-2"
                                                    data-testid={`open-${f.file_id}`}
                                                >
                                                    <Download size={14} /> Open in tab
                                                </button>
                                            </div>
                                            {isImage && (
                                                <div className="bg-black/40 border-t border-white/5 p-3 flex justify-center">
                                                    <img
                                                        src={url}
                                                        alt={f.original_filename}
                                                        className="max-h-96 max-w-full rounded-md object-contain"
                                                        data-testid={`preview-image-${f.file_id}`}
                                                    />
                                                </div>
                                            )}
                                            {isPdf && (
                                                <div className="bg-black/40 border-t border-white/5">
                                                    <iframe
                                                        src={url}
                                                        title={f.original_filename}
                                                        className="w-full h-[600px] bg-white"
                                                        data-testid={`preview-pdf-${f.file_id}`}
                                                    />
                                                </div>
                                            )}
                                        </li>
                                    );
                                })}
                            </ul>
                        )}
                    </div>
                </section>

                {/* Right column: notes + activity */}
                <aside className="space-y-6">
                    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="notes-panel">
                        <h2 className="font-display text-xl mb-3">Internal notes</h2>
                        <textarea
                            value={note}
                            onChange={(ev) => setNote(ev.target.value)}
                            rows={3}
                            placeholder="Add a note for your team…"
                            className="glow-input w-full px-3 py-2.5 rounded-xl text-sm resize-none"
                            data-testid="note-input"
                        />
                        <button
                            onClick={addNote}
                            disabled={savingNote || !note.trim()}
                            className="btn-gold w-full mt-2 py-2 text-sm inline-flex items-center justify-center gap-2"
                            data-testid="add-note-btn"
                        >
                            {savingNote ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
                            Add note
                        </button>

                        <div className="mt-5 space-y-3 max-h-80 overflow-y-auto pr-1" data-testid="notes-list">
                            {(data.internal_notes || []).length === 0 && (
                                <p className="text-xs text-white/40">No notes yet.</p>
                            )}
                            {(data.internal_notes || []).slice().reverse().map((n) => (
                                <div key={n.note_id} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                                    <div className="text-sm text-white/90 whitespace-pre-wrap">{n.text}</div>
                                    <div className="mt-1 text-[11px] text-white/40">
                                        {n.author} · {new Date(n.created_at).toLocaleString()}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6" data-testid="activity-panel">
                        <h2 className="font-display text-xl mb-3 flex items-center gap-2">
                            <Clock size={16} className="text-[#D4AF37]" /> Activity
                        </h2>
                        <ul className="space-y-2 max-h-72 overflow-y-auto">
                            {(data.activity_log || []).map((l) => (
                                <li key={l.log_id} className="text-xs text-white/70 border-l border-[#D4AF37]/30 pl-3 py-1">
                                    <div className="text-white/90">
                                        {l.action.replaceAll("_", " ")}
                                        {l.details?.from && l.details?.to && (
                                            <span className="text-white/50">
                                                : <span>{l.details.from}</span> → <span className="text-[#D4AF37]">{l.details.to}</span>
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-[10px] text-white/40 mt-0.5">
                                        {l.actor} · {new Date(l.timestamp).toLocaleString()}
                                    </div>
                                </li>
                            ))}
                            {(!data.activity_log || data.activity_log.length === 0) && (
                                <p className="text-xs text-white/40">No activity yet.</p>
                            )}
                        </ul>
                    </div>
                </aside>
            </main>
        </div>
    );
}
